import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { safeXhr } from '../lib/safeXhr';
import { hexToRgbTriplet, softenForSurface } from '../lib/brandColor';
import API_URL from '../lib/apiConfig';
import { useAuth } from './AuthContext';

/**
 * Per-tenant branding for the app shell.
 *
 * PATH: /api/admin/branding. The router is mounted under the admin prefix in
 * server.py (`api_router.include_router(org_branding_router, prefix="/admin")`)
 * even though reading it is no longer an admin-only action. The first version
 * of this file called /api/branding and 404'd silently, which fail-quiet
 * rendered as "this tenant has no branding" - indistinguishable from working.
 *
 * Roadmap Phase 2 item 9. Ryan, 9 Sep 2026: make the theme available in every
 * tenant, not just partners. The branding record (logo, colour, company name)
 * has been stored per tenant for a while and already reaches generated
 * documents — SOWs, time reports, certificates. This is what makes it reach
 * the app itself.
 *
 * FAIL-QUIET BY DESIGN. Every failure path lands on BOSS's own defaults:
 * no user, a request that errors, a tenant with no row, a blank field. A
 * customer seeing our logo instead of theirs is a cosmetic disappointment;
 * a customer seeing a broken shell because a branding fetch 500'd is an
 * outage. There is deliberately no error state exposed to the UI.
 */

const BrandingContext = createContext(null);

const DEFAULTS = {
  logoUrl: '/logo-3.png',
  productName: 'BOSS',
  primaryColor: null,
};

// Only accept colours we can safely inject into a CSS custom property.
// The value comes from a tenant admin's free-text field and lands in the
// document's style, so anything that isn't plainly a colour is dropped
// rather than escaped — the blast radius of a bad regex here is style
// injection on every page.
const SAFE_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;

export function BrandingProvider({ children }) {
  const [branding, setBranding] = useState(DEFAULTS);
  const { user } = useAuth();

  // Pulled out of the effect so the branding screen can call it after a save.
  // Without that, editing the name updated the database but nothing told the
  // shell, so the sidebar kept the old value until the page was refreshed.
  //
  // Refetch rather than reload: a full reload throws away app state and
  // flashes the whole UI to change one word in a sidebar.
  const loadBranding = useCallback(async () => {
    if (!user) {
      setBranding(DEFAULTS);
      return;
    }

    try {
      const token = localStorage.getItem('boss_session_token');
      const { data } = await safeXhr('GET', `${API_URL}/api/admin/branding`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!data) return;

      const color =
        typeof data.primary_color === 'string' &&
        SAFE_COLOR.test(data.primary_color.trim())
          ? data.primary_color.trim()
          : null;

      setBranding({
        logoUrl: data.logo_url || DEFAULTS.logoUrl,
        // display_name first: provider_name is the LEGAL entity used on SOW
        // documents, so a tenant that has only ever set that would otherwise
        // render "MetaData Engineering d/b/a SimpliMeta" in the sidebar. Kept
        // as a fallback so tenants who set provider_name before display_name
        // existed still get their own name.
        productName:
          data.display_name || data.provider_name || DEFAULTS.productName,
        primaryColor: color,
      });
    } catch {
      // Deliberately silent — see the note at the top of this file.
      setBranding(DEFAULTS);
    }
  }, [user]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  // Expose the colour as CSS custom properties so stylesheets can pick it up
  // without every component reading the context. ThemeContext sets the
  // light/dark class on the same element and does not touch these variables,
  // so the two are independent: a tenant's colour survives a theme toggle.
  //
  // Three properties, because the surfaces need different things:
  //
  //   --brand-primary      the raw colour, for anything wanting it solid
  //   --brand-primary-rgb  "139, 92, 246" so a stylesheet can compose its own
  //                        alpha: rgba(var(--brand-primary-rgb), 0.2)
  //   --brand-primary-soft lightness-corrected, for TEXT only
  //
  // The triplet exists so translucent decoration needs no color-mix() and no
  // JS branch: when the variable is unset the whole declaration is invalid and
  // the browser falls back to the preceding rule, which is the existing
  // violet. Unbranded tenants therefore take a code path that is not merely
  // equivalent to today's — it is today's.
  //
  // --brand-primary-soft is NOT the tenant's colour and must never be used for
  // decoration. It is derived to clear a measured contrast threshold against
  // the sidebar, which is what stops a tenant typing "#1a1a1a" into a settings
  // field and making their own navigation unreadable. See lib/brandColor.js.
  useEffect(() => {
    const root = document.documentElement;
    if (branding.primaryColor) {
      root.style.setProperty('--brand-primary', branding.primaryColor);
      root.style.setProperty(
        '--brand-primary-rgb',
        hexToRgbTriplet(branding.primaryColor),
      );
      root.style.setProperty(
        '--brand-primary-soft',
        softenForSurface(branding.primaryColor),
      );
    } else {
      root.style.removeProperty('--brand-primary');
      root.style.removeProperty('--brand-primary-rgb');
      root.style.removeProperty('--brand-primary-soft');
    }
  }, [branding.primaryColor]);

  return (
    <BrandingContext.Provider value={{ ...branding, refreshBranding: loadBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}

/** Branding for the current tenant. Always returns usable values.
 *
 * The fallback carries a no-op refreshBranding so a consumer rendered
 * outside the provider - a test, a stray mount - can still call it without
 * throwing. DEFAULTS alone would yield undefined and `await undefined()`.
 */
const _NO_PROVIDER = { ...DEFAULTS, refreshBranding: async () => {} };

export const useBranding = () => useContext(BrandingContext) || _NO_PROVIDER;

export { DEFAULTS as BRANDING_DEFAULTS };
